import React from "react";
import {
    SovosCard,
    SovosDivider,
    SovosTypography,
    SovosLinearProgress,
    SovosIcon,
} from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { stringifyFilter } from "colbi_web_ui/lib/utils";
import { CheckCircleRounded, ErrorRounded } from "mosaic-react-icons";

const mapper = (data, history, params, fiscalYearOfGenEntry, GeneratorStatus) => {
    const schemaType = "SCHEMA";

    return data?.map((elem, index) => {
        const prepareFilterToDataIntegrity = elem?.dataIntegrity?.ruleIds?.map((item) => {
            return { value: item };
        });
        const dataIntegrityFilter = prepareFilterToDataIntegrity
            ? stringifyFilter([{ column: "RuleID", filters: prepareFilterToDataIntegrity }])
            : null;

        const schemaValidationFilter = elem?.schema ? stringifyFilter([{ column: "RuleType", filters: [{ "value": schemaType }] }]) : null;

        // Show advanced steps if status is NOT 4, 5, or 8
        const shouldShowAdvancedSteps = ![4, 5, 8].includes(GeneratorStatus?.value);

        return {
            entity: elem.entity,
            status: elem.status,
            dataIntegrity: elem.dataIntegrity?.status,
            auditStatus: elem.auditStatus,
            id: index,
            card: [
                [
                    {
                        labels: ["Entity"],
                        value: {
                            entity: elem.entity,
                            isMandatory: elem.isMandatory,
                            customComponent: "GeneratorEntity",
                        },
                        type: "customComponent",
                        align: "left",
                    },
                ],
                [
                    {
                        labels: ["Data Presence"],
                        value: {
                            status: elem.status,
                            isMandatory: elem.isMandatory,
                            history,
                            customComponent: "GeneratorDataProvide",
                        },
                        align: "left",
                    },
                ],
                [
                    {
                        labels: ["Separator"],
                        value: {
                            customComponent: "SeparatorArrow",
                        },
                    }
                ],
                [
                    {
                        labels: ["Data Integrity"],
                        value: {
                            auditStatus: elem?.dataIntegrity?.status === 2 ? null : elem?.dataIntegrity?.status,
                            uncheckedDescription: elem?.dataIntegrity?.status === 2 ? "No_Rules_Applied" : null,
                            history,
                            link: `/${params.locale}/${params.projectId}/${fiscalYearOfGenEntry || params.fiscalYear}/full/nc_audit/${dataIntegrityFilter ? "?filter=" + dataIntegrityFilter : ""}`,
                            customComponent: "GeneratorAuditStatus",
                        },
                        type: "string",
                    },
                ],
                [
                    {
                        labels: ["Separator"],
                        value: {
                            customComponent: "SeparatorArrow",
                        },
                    }
                ],
                [
                    {
                        labels: ["Schema Validation"],
                        value: shouldShowAdvancedSteps ? {
                            auditStatus: elem?.schema,
                            history,
                            link: `/${params.locale}/${params.projectId}/${fiscalYearOfGenEntry || params.fiscalYear}/full/nc_audit/${schemaValidationFilter ? "?filter=" + schemaValidationFilter : ""}`,
                            customComponent: "GeneratorAuditStatus",
                        } : {
                            // Empty/placeholder value for uncompleted step
                            auditStatus: null,
                            history,
                            customComponent: "GeneratorAuditStatus",
                        },
                        type: "string",
                    },
                ],
                [
                    {
                        labels: ["Separator"],
                        value: {
                            customComponent: "SeparatorArrow",
                        },
                    }
                ],
                [
                    {
                        labels: ["Audit Status"],
                        value: shouldShowAdvancedSteps ? {
                            auditStatus: elem.auditStatus,
                            history,
                            link: `/${params.locale}/${params.projectId}/${fiscalYearOfGenEntry || params.fiscalYear}/full/nc_audit`,
                            customComponent: "GeneratorAuditStatus",
                        } : {
                            // Empty/placeholder value for uncompleted step
                            auditStatus: null,
                            history,
                            customComponent: "GeneratorAuditStatus",
                        },
                        align: "left",
                    },
                ],
            ],
        };
    });
};

const generateMessages = (Status, i18n) => {
    switch (Status) {
        case 4:
            return {
                mainMessage: i18n`Pre validation Success`,
                detailsMessage: i18n`All mandatory files are provided and not exists potential critical audit finding`,
                percentage: 25,
                icon: (
                    <SovosIcon color={"success"} sx={{ marginRight: "10px" }}>
                        {<CheckCircleRounded />}
                    </SovosIcon>
                ),
            };
        case 5:
            return {
                mainMessage: i18n`Pre validation Failed`,
                detailsMessage: "",
                percentage: 25,
            };
        case 6:
            return {
                mainMessage: i18n`Data Integrity Success`,
                detailsMessage: i18n`Data have a correct integrity`,
                percentage: 50,
                icon: (
                    <SovosIcon color={"success"} sx={{ marginRight: "10px" }}>
                        {<CheckCircleRounded />}
                    </SovosIcon>
                ),
            };
        case 7:
            return {
                mainMessage: i18n`Data Integrity Failed`,
                detailsMessage: i18n`Data have a incorrect integrity`,
                percentage: 50,
                icon: (
                    <SovosIcon color={"error"} sx={{ marginRight: "10px" }}>
                        {<ErrorRounded />}
                    </SovosIcon>
                ),
            };
        case 8:
            return {
                mainMessage: i18n`Data Integrity In Progress`,
                detailsMessage: i18n`Checking if data have the correct integrity`,
                percentage: 50,
            };
        case 1:
            return {
                mainMessage: i18n`Your file are ready`,
                detailsMessage: i18n`Your file generated successfully`,
                percentage: 100,
                icon: (
                    <SovosIcon color={"success"} sx={{ marginRight: "10px" }}>
                        {<CheckCircleRounded />}
                    </SovosIcon>
                ),
            };
        case 2:
            return {
                mainMessage: i18n`Your generation failed`,
                detailsMessage: i18n`Your generation failed, please check the error message`,
                percentage: 100,
                icon: (
                    <SovosIcon color={"error"} sx={{ marginRight: "10px" }}>
                        {<ErrorRounded />}
                    </SovosIcon>
                ),
            };
        default:
            return {
                mainMessage: i18n`We are processing your file...`,
                detailsMessage: "",
                percentage: 50,
            };
    }
};
//If the name of this function is changed, it must also be updated in the GeneratorDataFile model in the web-api.
const GeneratorListDetails = (props) => {
    const { history, i18n, params } = useGlobalValue();
    const { detailPanel, Status, fiscalYear: fiscalYearOfGenEntry } = props || {};
    const { data } = detailPanel || {};
    const { mainMessage, detailsMessage, percentage, icon } = generateMessages(Status?.value, i18n);

    return (
        <SovosCard sx={{ margin: "20px" }}>
            <SovosTypography variant="h3" sx={{ display: "flex", alignItems: "center" }}>
                {icon && icon}
                <b>{mainMessage}</b>
            </SovosTypography>

            <SovosDivider sx={{ marginTop: "15px", marginBottom: "15px" }} />

            <SovosTypography sx={{ marginBottom: "10px" }}>
                <b>{detailsMessage}</b>
            </SovosTypography>

            <SovosTypography>{percentage + "%"}</SovosTypography>
            <SovosLinearProgress
                value={percentage}
                variant="determinate"
                aria-label="Progress pre validation check"
            />

            <SovosDivider sx={{ marginTop: "15px", marginBottom: "15px" }} />

            <DataGrid
                id="results"
                card={rows.CellCard}
                items={mapper(data, history, params, fiscalYearOfGenEntry, Status)}
                header={[
                    {
                        label: ["Entity"],
                        basis: "40%",
                        type: "customComponent",
                        columnName: "entity",
                        align: "left",
                    },
                    {
                        label: ["Data Presence"],
                        columnName: "status",
                        type: "customComponent",
                        align: "left",
                    },
                    {
                        label: ["arrowDataPresence"],
                        columnName: "arrowDataPresence",
                        type: "customComponent",
                        align: "center",
                        resizable: false,
                        disableReorder: true,
                        basis: "5%",
                        disableColumnMenu: true,
                        renderHeader: () => <SovosTypography sx={{ display: "none" }} />,
                    },
                    {
                        label: ["Data Integrity"],
                        columnName: "dataIntegrity",
                        type: "customComponent",
                        align: "left",
                    },
                    {
                        label: ["arrowDataIntegrity"],
                        columnName: "arrowDataIntegrity",
                        type: "customComponent",
                        align: "center",
                        resizable: false,
                        disableReorder: true,
                        basis: "5%",
                        disableColumnMenu: true,
                        renderHeader: () => <SovosTypography sx={{ display: "none" }} />,
                    },
                    {
                        label: ["Schema Validation"],
                        columnName: "schemaValidation",
                        type: "customComponent",
                        align: "left",
                    },
                    {
                        label: ["arrowSchemaValidation"],
                        columnName: "arrowSchemaValidation",
                        type: "customComponent",
                        align: "center",
                        resizable: false,
                        disableReorder: true,
                        basis: "5%",
                        disableColumnMenu: true,
                        renderHeader: () => <SovosTypography sx={{ display: "none" }} />,
                    },
                    {
                        label: ["Audit Status"],
                        columnName: "auditStatus",
                        type: "customComponent",
                        align: "left",
                    },
                ]}
                pageSize={100}
                metadata={{ total: data?.length }}
                status={{ loading: false }}
            />
            {Status?.value === 1 && (
                <>
                    <SovosDivider sx={{ marginTop: "15px", marginBottom: "15px" }} />
                    <SovosTypography variant="h3">
                        <b>{i18n`Process Done`}</b>
                    </SovosTypography>
                </>
            )}
        </SovosCard>
    );
};

export default GeneratorListDetails;
